import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Save,
  Trash2,
  RefreshCw,
  ArrowLeft,
  Image,
  X,
  Plus,
} from "lucide-react";
import ImageUploadSection from "./ImgCropper";
import CategorySelect from "../../../components/selectCategory/SelectCategory";
import DescriptionEditor from "../../../components/descriptionEditor/DescriptionEditor";
import EditSkelton from "./EditSkelton";

const baseURL = process.env.REACT_APP_API_BASE_URL;

const ProductEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [productData, setProductData] = useState({
    productDetails: {
      title: "",
      price: 0,
      discountPrice: 0,
      description: "",
      status: 1,
    },
    subProducts: [],
    images: [],
  });
  const [categoryId, setCategoryId] = useState(null);
  const [description, setDescription] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [deletingImageId, setDeletingImageId] = useState(null);
  const [deletingSubProductId, setDeletingSubProductId] = useState(null);
  const [isAddingSubProduct, setIsAddingSubProduct] = useState(false);
  const [newSubProduct, setNewSubProduct] = useState({
    title: "",
    price: 0,
  });
  const [showImageUpload, setShowImageUpload] = useState(false);

  const fetchProductDetails = async () => {
    try {
      const response = await fetch(`${baseURL}/api/products/${id}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch product: ${response.status}`);
      }

      const data = await response.json();
      setProductData(data);
      setCategoryId(data?.productDetails?.categoryId);
      setDescription(data?.productDetails?.description);
      setLoading(false);
    } catch (err) {
      setError(`Error loading product: ${err.message}`);
      setLoading(false);
    }
  };

  // Fetch product data
  useEffect(() => {
    if (id) {
      fetchProductDetails();
    }
  }, [id]);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    setProductData({
      ...productData,
      productDetails: {
        ...productData.productDetails,
        [name]: processedValue,
      },
    });
  };

  // Handle new sub-product form changes
  const handleSubProductChange = (e) => {
    const { name, value } = e.target;
    setNewSubProduct({
      ...newSubProduct,
      [name]: value,
    });
  };

  // Save product changes
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccessMessage("");

    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${baseURL}/api/products/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: productData.productDetails.title,
          price: productData.productDetails.price,
          discountPrice: productData.productDetails.discountPrice,
          description: description,
          categoryId: categoryId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update product: ${response.status}`);
      }

      setSuccessMessage("Product updated successfully");
      setSaving(false);
    } catch (err) {
      setError(`Error saving product: ${err.message}`);
      setSaving(false);
    }
  };

  // Delete product (set status to inactive)
  const handleDeleteProduct = async () => {
    if (!window.confirm("Are you sure you want to delete this product?")) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage("");

    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${baseURL}/api/products/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: 0 }),
      });

      if (!response.ok) {
        throw new Error(`Failed to delete product: ${response.status}`);
      }

      setProductData((prev) => ({
        ...prev,
        productDetails: {
          ...prev.productDetails,
          status: 0,
        },
      }));
      setSuccessMessage("Product deleted successfully");
      setSaving(false);
    } catch (err) {
      setError(`Error deleting product: ${err.message}`);
      setSaving(false);
    }
  };

  // Restore product (set status to active)
  const handleRestoreProduct = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage("");

    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${baseURL}/api/products/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: 1 }),
      });

      if (!response.ok) {
        throw new Error(`Failed to restore product: ${response.status}`);
      }

      setProductData((prev) => ({
        ...prev,
        productDetails: {
          ...prev.productDetails,
          status: 1,
        },
      }));
      setSuccessMessage("Product restored successfully");
      setSaving(false);
    } catch (err) {
      setError(`Error restoring product: ${err.message}`);
      setSaving(false);
    }
  };

  // Delete image
  const handleDeleteImage = async (imageId) => {
    if (!window.confirm("Are you sure you want to delete this image?")) {
      return;
    }

    setDeletingImageId(imageId);
    setError(null);
    setSuccessMessage("");

    try {
      // Get token from session storage
      const token = sessionStorage.getItem("token");

      if (!token) {
        throw new Error("Authentication token not found. Please log in again.");
      }

      const response = await fetch(
        `${baseURL}/api/products/img/${imageId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete image: ${response.status}`);
      }

      // Update the product data by removing the deleted image
      setProductData((prevData) => ({
        ...prevData,
        images: prevData.images.filter((img) => img.productImageId !== imageId),
      }));

      setSuccessMessage("Image deleted successfully");
    } catch (err) {
      setError(`Error deleting image: ${err.message}`);
    } finally {
      setDeletingImageId(null);
    }
  };

  // Handle image upload success
  const handleImageUploadSuccess = (message) => {
    setSuccessMessage(message);
    setShowImageUpload(false);
    // Refetch product details to get updated images
    fetchProductDetails();
  };

  // Handle image upload error
  const handleImageUploadError = (errorMessage) => {
    setError(errorMessage);
  };

  // Add a new sub-product
  const handleAddSubProduct = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage("");
    setIsAddingSubProduct(true);

    try {
      // Get token from session storage
      const token = sessionStorage.getItem("token");

      if (!token) {
        throw new Error("Authentication token not found. Please log in again.");
      }

      const payload = {
        title: newSubProduct.title,
        price: parseFloat(newSubProduct.price),
        mainProductId: parseInt(id),
      };

      const response = await fetch(`${baseURL}/api/products/sub`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to add sub-product: ${response.status}`);
      }

      //refresh
      fetchProductDetails();
      // Reset the form
      setNewSubProduct({ title: "", price: 0 });
      setSuccessMessage("Sub-product added successfully");
    } catch (err) {
      setError(`Error adding sub-product: ${err.message}`);
    } finally {
      setIsAddingSubProduct(false);
    }
  };

  // Delete/Restore sub-product
  const handleToggleSubProductStatus = async (subProductId, newStatus) => {
    const action = newStatus === 0 ? "delete" : "restore";
    if (
      newStatus === 0 &&
      !window.confirm("Are you sure you want to delete this variant?")
    ) {
      return;
    }

    setDeletingSubProductId(subProductId);
    setError(null);
    setSuccessMessage("");

    try {
      // Get token from session storage
      const token = sessionStorage.getItem("token");

      if (!token) {
        throw new Error("Authentication token not found. Please log in again.");
      }

      const response = await fetch(
        `${baseURL}/api/products/sub/${subProductId}?status=${newStatus}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to ${action} sub-product: ${response.status}`);
      }

      // Update the product data by updating the status of the sub-product
      setProductData((prevData) => ({
        ...prevData,
        subProducts: prevData?.subProducts?.map((subProd) =>
          subProd.subProductId === subProductId
            ? { ...subProd, status: newStatus }
            : subProd
        ),
      }));

      setSuccessMessage(`Sub-product ${action}d successfully`);
    } catch (err) {
      setError(`Error ${action}ing sub-product: ${err.message}`);
    } finally {
      setDeletingSubProductId(null);
    }
  };

  // Format price
  const formatPrice = (price) => {
    return parseFloat(price).toLocaleString("en-US");
  };

  const handleDescriptionChange = (htmlContent) => {
    setDescription(htmlContent);
  }

  console.log("cat" + categoryId);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-_white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6 sm:px-6 lg:px-8 flex flex-col sm:flex-row sm:justify-between sm:items-center">
          <div className="flex items-center mb-4 sm:mb-0">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 rounded-full hover:bg-_gray_hover transition-colors duration-200"
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5 text-_dark" />
            </button>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-_dark truncate">
              {loading
                ? "Loading Product..."
                : `Edit: ${productData.productDetails.title}`}
            </h1>
          </div>
          <div className="flex items-center">
            {productData.productDetails.status === 0 ? (
              <button
                onClick={handleRestoreProduct}
                disabled={saving}
                className="flex items-center px-3 py-2 text-sm font-medium bg-_blue bg-opacity-5 text-_blue rounded-md hover:opacity-90 transition-opacity duration-200 disabled:opacity-5"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Restore</span>
              </button>
            ) : (
              <button
                onClick={handleDeleteProduct}
                disabled={saving}
                className="flex items-center px-3 py-2 text-sm font-medium bg-_red bg-opacity-5 text-_red rounded-md hover:opacity-90 transition-opacity duration-200 disabled:opacity-5"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Delete</span>
              </button>
            )}
          </div>
        </div>
      </header>
  
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {loading ? (
          <EditSkelton/>
        ) : error ? (
          <div className="bg-_white p-4 rounded-lg border-l-4 border-_red">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-_red">Error</h3>
                <div className="mt-2 text-sm text-_dark">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Success Message */}
            {successMessage && (
              <div className="bg-_white p-4 rounded-lg border-l-4 border-_bg_lite shadow-sm">
                <div className="flex">
                  <div className="ml-3">
                    <p className="text-sm font-medium text-_dark">
                      {successMessage}
                    </p>
                  </div>
                </div>
              </div>
            )}
  
            {/* Product Images */}
            <div className="bg-_white shadow-sm rounded-lg overflow-hidden border border-_gray_lite">
              <div className="px-4 sm:px-6 py-4 border-b border-_gray_lite flex flex-col sm:flex-row sm:justify-between sm:items-center">
                <h2 className="text-lg font-medium text-_dark mb-2 sm:mb-0">
                  Product Images
                </h2>
                <button
                  onClick={() => setShowImageUpload(!showImageUpload)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-_white bg-_blue hover:bg-_bg_dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-_blue transition-colors duration-200"
                >
                  {showImageUpload ? (
                    <>
                      <X className="mr-1 h-4 w-4" />
                      Cancel
                    </>
                  ) : (
                    <>
                      <Plus className="mr-1 h-4 w-4" />
                      Add Image
                    </>
                  )}
                </button>
              </div>
  
              {/* Image Upload Section */}
              {showImageUpload && (
                <div className="p-4 sm:p-6 border-b border-_gray_lite bg-_gray_hover">
                  <ImageUploadSection
                    productId={id}
                    onSuccess={handleImageUploadSuccess}
                    onError={handleImageUploadError}
                  />
                </div>
              )}
  
              <div className="p-4 sm:p-6">
                {productData?.images?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-4 sm:p-6 text-center bg-_gray_hover rounded-lg">
                    <Image className="h-12 w-12 text-_gray mb-2" />
                    <h3 className="text-sm font-medium text-_dark">
                      No images available
                    </h3>
                    <p className="text-sm text-_dark_lite mt-1">
                      Upload images to showcase your product
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
                    {productData?.images?.map((image) => (
                      <div
                        key={image.productImageId}
                        className="relative group"
                      >
                        <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-md bg-_gray_lite">
                          <img
                            src={image.imageUrl}
                            alt={`Product image ${image.productImageId}`}
                            className="h-full w-full object-cover object-center"
                          />
                        </div>
                        <button
                          onClick={() =>
                            handleDeleteImage(image.productImageId)
                          }
                          disabled={deletingImageId === image.productImageId}
                          className={`absolute top-2 right-2 p-1 rounded-full bg-_red text-_white opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${
                            deletingImageId === image.productImageId
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          aria-label="Delete image"
                        >
                          {deletingImageId === image.productImageId ? (
                            <div className="h-4 w-4 border-t-2 border-b-2 border-_white rounded-full animate-spin"></div>
                          ) : (
                            <X className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
  
            {/* Product Details */}
            <div className="bg-_white shadow-sm rounded-lg overflow-hidden border border-_gray_lite">
              <div className="px-4 sm:px-6 py-4 border-b border-_gray_lite flex justify-between items-center">
                <h2 className="text-lg font-medium text-_dark">
                  Product Details
                </h2>
                <div>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      productData.productDetails.status === 1
                        ? "bg-_bg_lite text-_white"
                        : "bg-_red text-_white"
                    }`}
                  >
                    {productData.productDetails.status === 1
                      ? "Active"
                      : "Inactive"}
                  </span>
                </div>
              </div>
  
              {/* Form */}
              <form onSubmit={handleSave}>
                <div className="px-4 sm:px-6 py-4 space-y-4 sm:space-y-6">
                  <div>
                    <label
                      htmlFor="title"
                      className="block text-sm font-medium text-_dark mb-1"
                    >
                      Product Title
                    </label>
                    <input
                      type="text"
                      name="title"
                      id="title"
                      value={productData.productDetails.title || ""}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-_gray_lite rounded-md shadow-sm focus:outline-none focus:ring-_blue focus:border-_blue"
                      required
                    />
                  </div>
  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label
                        htmlFor="price"
                        className="block text-sm font-medium text-_dark mb-1"
                      >
                        Price
                      </label>
                      <div className="relative mt-1 rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-_gray sm:text-sm"></span>
                        </div>
                        <input
                          type="number"
                          name="price"
                          id="price"
                          min="0"
                          step="0.01"
                          value={productData.productDetails.price || ""}
                          onChange={handleChange}
                          className="block w-full pl-7 pr-3 py-2 border border-_gray_lite rounded-md shadow-sm focus:outline-none focus:ring-_blue focus:border-_blue"
                          required
                        />
                      </div>
                    </div>
  
                    <div>
                      <label
                        htmlFor="discountPrice"
                        className="block text-sm font-medium text-_dark mb-1"
                      >
                        Discount Price (Optional)
                      </label>
                      <div className="relative mt-1 rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-_gray sm:text-sm"></span>
                        </div>
                        <input
                          type="number"
                          name="discountPrice"
                          id="discountPrice"
                          min="0"
                          step="0.01"
                          value={productData.productDetails.discountPrice || ""}
                          onChange={handleChange}
                          className="block w-full pl-7 pr-3 py-2 border border-_gray_lite rounded-md shadow-sm focus:outline-none focus:ring-_blue focus:border-_blue"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-_dark mb-1">Category</label>
                    <CategorySelect
                      onCategorySelect={setCategoryId}
                      initialCategoryId={productData.productDetails.categoryId}
                    />
                  </div>
                  <div className="form-group">
                    <label
                      htmlFor="description"
                      className="block text-sm font-medium text-_dark mb-1"
                    >
                      Product Description
                    </label>
                    <DescriptionEditor initialDescription={description} onChange={handleDescriptionChange} />
                  </div>
                </div>
  
                <div className="px-4 sm:px-6 py-4 bg-_gray_hover text-right">
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-_white bg-_blue hover:bg-_bg_dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-_blue transition-colors duration-200 disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <div className="mr-2 h-4 w-4 border-t-2 border-b-2 border-_white rounded-full animate-spin"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
  
            {/* Sub Products Section */}
            <div className="bg-_white shadow-sm rounded-lg overflow-hidden border border-_gray_lite">
              <div className="px-4 sm:px-6 py-4 border-b border-_gray_lite flex justify-between items-center">
                <h2 className="text-lg font-medium text-_dark">
                  Variants / Sub Products
                </h2>
              </div>
  
              {/* Add Sub Product Form */}
              {1 && (
                <div className="px-4 sm:px-6 py-4 bg-_gray_hover border-b border-_gray_lite">
                  <form onSubmit={handleAddSubProduct} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="subProductTitle"
                          className="block text-sm font-medium text-_dark mb-1"
                        >
                          Variant Title
                        </label>
                        <input
                          type="text"
                          name="title"
                          id="subProductTitle"
                          value={newSubProduct.title}
                          onChange={handleSubProductChange}
                          className="block w-full px-3 py-2 border border-_gray_lite rounded-md shadow-sm focus:outline-none focus:ring-_blue focus:border-_blue"
                          required
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="subProductPrice"
                          className="block text-sm font-medium text-_dark mb-1"
                        >
                          Variant Price
                        </label>
                        <div className="relative mt-1 rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-_gray sm:text-sm"></span>
                          </div>
                          <input
                            type="number"
                            name="price"
                            id="subProductPrice"
                            min="0"
                            step="0.01"
                            value={newSubProduct.price}
                            onChange={handleSubProductChange}
                            className="block w-full pl-7 pr-3 py-2 border border-_gray_lite rounded-md shadow-sm focus:outline-none focus:ring-_blue focus:border-_blue"
                            required
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setIsAddingSubProduct(false)}
                        className="inline-flex items-center justify-center px-3 py-2 border border-_gray_lite shadow-sm text-sm leading-4 font-medium rounded-md text-_dark bg-_white hover:bg-_gray_hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-_blue transition-colors duration-200"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isAddingSubProduct && !newSubProduct.title}
                        className="inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-_white bg-_blue hover:bg-_bg_dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-_blue transition-colors duration-200 disabled:opacity-50"
                      >
                        {isAddingSubProduct ? (
                          <>
                            <div className="mr-2 h-4 w-4 border-t-2 border-b-2 border-_white rounded-full animate-spin"></div>
                            Adding...
                          </>
                        ) : (
                          <>
                            <Plus className="mr-1 h-4 w-4" />
                            Add Variant
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}
  
              {/* Sub Products Table */}
              {productData?.subProducts &&
              productData?.subProducts?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-_gray_lite">
                    <thead className="bg-_gray_hover">
                      <tr>
                        <th
                          scope="col"
                          className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-_dark uppercase tracking-wider"
                        >
                          Title
                        </th>
                        <th
                          scope="col"
                          className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-_dark uppercase tracking-wider"
                        >
                          Price
                        </th>
                        <th
                          scope="col"
                          className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-_dark uppercase tracking-wider hidden sm:table-cell"
                        >
                          Status
                        </th>
                        <th
                          scope="col"
                          className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-_dark uppercase tracking-wider hidden md:table-cell"
                        >
                          Created At
                        </th>
                        <th
                          scope="col"
                          className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-_dark uppercase tracking-wider"
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-_white divide-y divide-_gray_lite">
                      {productData?.subProducts?.map((subProduct) => (
                        <tr key={subProduct.subProductId} className="hover:bg-_gray_hover transition-colors duration-150">
                          <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-_dark">
                            {subProduct.title}
                          </td>
                          <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-_dark_lite">
                            LKR {formatPrice(subProduct.price)}
                          </td>
                          <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-_dark_lite hidden sm:table-cell">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                subProduct.status === 1
                                  ? "bg-_bg_lite text-_white"
                                  : "bg-_red text-_white"
                              }`}
                            >
                              {subProduct.status === 1 ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-_dark_lite hidden md:table-cell">
                            {new Date(
                              subProduct.createdAt
                            ).toLocaleDateString()}
                          </td>
                          <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            {deletingSubProductId ===
                            subProduct.subProductId ? (
                              <span className="text-_gray">
                                <div className="inline-block h-4 w-4 border-t-2 border-b-2 border-_blue rounded-full animate-spin"></div>
                              </span>
                            ) : subProduct.status === 1 ? (
                              <button
                                onClick={() =>
                                  handleToggleSubProductStatus(
                                    subProduct.subProductId,
                                    0
                                  )
                                }
                                className="text-_red hover:opacity-80 transition-opacity duration-200"
                              >
                                Delete
                              </button>
                            ) : (
                              <button
                                onClick={() =>
                                  handleToggleSubProductStatus(
                                    subProduct.subProductId,
                                    1
                                  )
                                }
                                className="text-_bg_lite hover:text-_blue transition-colors duration-200"
                              >
                                Restore
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-4 sm:p-6 text-center text-_dark_lite bg-_gray_hover rounded-lg m-4">
                  No variants found for this product. Add one to get started.
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );

};

export default ProductEditPage;